import React, { useState, useEffect, useRef } from 'react';
import ProgressBar from './ProgressBar.jsx';
import LoadingSpinner from './LoadingSpinner.jsx';
import ErrorDisplay from './ErrorDisplay.jsx';

/**
 * InitializationProgress Component
 * Provides detailed progress feedback during initialization with step-by-step tracking
 * Enhanced with cross-browser compatibility and accessibility features
 */
const InitializationProgress = ({ 
  initializationState,
  onRetry,
  onFallback,
  onCancel,
  className=''
}) => {
  const [timeoutWarning, setTimeoutWarning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [lastAnnouncedStep, setLastAnnouncedStep] = useState('');
  const [lastAnnouncedProgress, setLastAnnouncedProgress] = useState(0);
  
  // Refs for accessibility
  const liveRegionRef = useRef(null);
  const statusRef = useRef(null);
  const retryButtonRef = useRef(null);
  const fallbackButtonRef = useRef(null);
  const cancelButtonRef = useRef(null);

  // Track elapsed time and show timeout warnings
  useEffect(() => {
    if (initializationState.status === 'loading' || initializationState.status === 'retrying') {
      const startTime = initializationState.startTime || Date.now();
      
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        setElapsedTime(elapsed);
        
        // Show timeout warning after 15 seconds
        if (elapsed > 15000 && !timeoutWarning) {
          setTimeoutWarning(true);
        }
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setTimeoutWarning(false);
      setElapsedTime(0);
    }
  }, [initializationState.status, initializationState.startTime, timeoutWarning]);

  // Accessibility: Announce progress changes to screen readers
  useEffect(() => {
    const currentStep = initializationState.currentStep || '';
    const currentProgress = initializationState.progress || 0;
    
    // Only announce significant changes to avoid overwhelming screen readers
    const shouldAnnounce = (
      currentStep !== lastAnnouncedStep || 
      Math.abs(currentProgress - lastAnnouncedProgress) >= 20 ||
      initializationState.status === 'error' ||
      initializationState.status === 'retrying'
    );
    
    if (shouldAnnounce && liveRegionRef.current) {
      let announcement = '';
      
      if (initializationState.status === 'error') {
        announcement = `Loading failed: ${initializationState.error?.message || 'An error occurred'}`;
      } else if (initializationState.status === 'retrying') {
        announcement = `Retrying, attempt ${initializationState.retryCount} of 3`;
      } else if (currentStep) {
        announcement = `Loading progress: ${currentProgress}% complete. ${currentStep}`;
      }
      
      if (announcement) {
        // Use a slight delay to ensure the DOM is updated
        setTimeout(() => {
          if (liveRegionRef.current) {
            liveRegionRef.current.textContent = announcement;
          }
        }, 100);
        
        setLastAnnouncedStep(currentStep);
        setLastAnnouncedProgress(currentProgress);
      }
    }
  }, [initializationState.currentStep, initializationState.progress, initializationState.status, 
      initializationState.error, initializationState.retryCount, lastAnnouncedStep, lastAnnouncedProgress]);

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle keyboard navigation when error state is active
      if (initializationState.status !== 'error') return;
      
      switch (event.key) {
        case 'r':
        case 'R':
          if (event.ctrlKey || event.metaKey) return; // Don't interfere with browser refresh
          if (onRetry && retryButtonRef.current && !retryButtonRef.current.disabled) {
            event.preventDefault();
            retryButtonRef.current.focus();
            onRetry();
          }
          break;
        case 'f':
        case 'F':
          if (onFallback && fallbackButtonRef.current) {
            event.preventDefault();
            fallbackButtonRef.current.focus();
            onFallback();
          }
          break;
        case 'Escape':
          if (onCancel && cancelButtonRef.current) {
            event.preventDefault();
            cancelButtonRef.current.focus();
            onCancel();
          }
          break;
        case 'Tab':
          // Ensure proper tab order within the error state
          if (event.shiftKey) {
            // Shift+Tab - reverse tab order
            const focusableElements = [retryButtonRef.current, fallbackButtonRef.current, cancelButtonRef.current]
              .filter(el => el && !el.disabled);
            const currentIndex = focusableElements.indexOf(document.activeElement);
            if (currentIndex > 0) {
              event.preventDefault();
              focusableElements[currentIndex - 1].focus();
            }
          } else {
            // Tab - forward tab order
            const focusableElements = [retryButtonRef.current, fallbackButtonRef.current, cancelButtonRef.current]
              .filter(el => el && !el.disabled);
            const currentIndex = focusableElements.indexOf(document.activeElement);
            if (currentIndex >= 0 && currentIndex < focusableElements.length - 1) {
              event.preventDefault();
              focusableElements[currentIndex + 1].focus();
            }
          }
          break;
      }
    };

    if (initializationState.status === 'error') {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [initializationState.status, onRetry, onFallback, onCancel]);

  // Focus management for error state
  useEffect(() => {
    if (initializationState.status === 'error') {
      // Focus the first available action button when error state is entered
      const focusTarget = retryButtonRef.current || fallbackButtonRef.current || cancelButtonRef.current;
      if (focusTarget) {
        setTimeout(() => focusTarget.focus(), 100);
      }
    }
  }, [initializationState.status]);

  // Format elapsed time for display
  const formatElapsedTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) {
      return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Get step icon based on status
  const getStepIcon = (status) => {
    switch (status) {
      case 'loading':
      case 'retrying':
        return (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 dark:border-blue-500" />
        );
      case 'success':
        return (
          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return (
          <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600" />
        );
    }
  };

  // Get progress steps based on current progress
  const getProgressSteps = () => {
    const steps = [
      { id: 1, name: 'Preparing', progress: 0 },
      { id: 2, name: 'Validating access', progress: 20 },
      { id: 3, name: 'Loading configuration', progress: 40 },
      { id: 4, name: 'Processing data', progress: 60 },
      { id: 5, name: 'Finalizing', progress: 80 },
      { id: 6, name: 'Ready', progress: 100 }
    ];

    return steps.map(step => ({
      ...step,
      status: initializationState.progress >= step.progress ? 
        (initializationState.progress === step.progress && initializationState.status === 'loading' ? 'loading' :
         initializationState.progress > step.progress ? 'success' : 'pending') : 'pending'
    }));
  };

  const steps = getProgressSteps();

  if (initializationState.status === 'success') {
    return null; // Don't show progress when successful
  }

  return (
    <div 
      className={`bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 ${className}`}
      role="region"
      aria-label="Parrot configuration loading status"
    >
      {/* Screen reader live region for progress announcements */}
      <div
        ref={liveRegionRef}
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      />
      
      {/* Status region for screen readers */}
      <div
        ref={statusRef}
        role="status"
        aria-live="polite"
        className="sr-only"
      >
        {initializationState.status === 'error' ? 'Loading failed' : 
         initializationState.status === 'retrying' ? 'Retrying connection' : 
         initializationState.status === 'loading' ? 'Loading in progress' :
         'Loading parrot configuration'}
      </div>

      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <h3 
            className="text-lg font-medium text-gray-900 dark:text-white mb-2"
            id="initialization-title"
          >
            {initializationState.status === 'error' ? 'Loading Failed' : 
             initializationState.status === 'retrying' ? 'Retrying...' : 
             'Loading Parrot Configuration'}
          </h3>
          {initializationState.status !== 'error' && (
            <p 
              className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600"
              id="initialization-description"
            >
              Please wait while we prepare your parrot editor
            </p>
          )}
        </div>

        {/* Enhanced Error Display */}
        {initializationState.status === 'error' && (
          <div role="alert" aria-describedby="initialization-title">
            <ErrorDisplay
              error={initializationState.error}
              onRetry={onRetry}
              onFallback={onFallback}
              onCancel={onCancel}
              showDetails={true}
              retryCount={initializationState.retryCount}
              maxRetries={3}
              retryButtonRef={retryButtonRef}
              fallbackButtonRef={fallbackButtonRef}
              cancelButtonRef={cancelButtonRef}
            />
          </div>
        )}

        {/* Progress Display */}
        {(initializationState.status === 'loading' || initializationState.status === 'retrying') && (
          <div 
            className="space-y-4"
            role="progressbar"
            aria-valuenow={initializationState.progress}
            aria-valuemin="0"
            aria-valuemax="100"
            aria-label={`Loading progress: ${initializationState.progress}% complete`}
            aria-describedby="current-step-description"
          >
            {/* Main Progress Bar */}
            <ProgressBar
              progress={initializationState.progress}
              currentStep={initializationState.currentStep}
              showPercentage={true}
              showStep={true}
              size="lg"
              color="blue"
              animated={true}
              aria-label={`Progress: ${initializationState.progress}%`}
            />
            
            {/* Current step description for screen readers */}
            <div id="current-step-description" className="sr-only">
              Current step: {initializationState.currentStep}
            </div>

            {/* Detailed Step Progress */}
            <div 
              className="space-y-2"
              role="list"
              aria-label="Loading steps"
            >
              {steps.map((step) => (
                <div 
                  key={step.id} 
                  className="flex items-center space-x-3"
                  role="listitem"
                  aria-label={`Step ${step.id}: ${step.name} - ${
                    step.status === 'success' ? 'completed' :
                    step.status === 'loading' ? 'in progress' :
                    'pending'
                  }`}
                >
                  <div 
                    className="flex-shrink-0"
                    aria-hidden="true"
                  >
                    {getStepIcon(step.status)}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm ${
                      step.status === 'success' ? 'text-green-600 dark:text-green-400' :
                      step.status === 'loading' ? 'text-blue-600 dark:text-blue-400 font-medium' :
                      'text-gray-500 dark:text-gray-400'
                    }`}>
                      {step.name}
                      <span className="sr-only">
                        {step.status === 'success' ? ' - completed' :
                         step.status === 'loading' ? ' - in progress' :
                         ' - pending'}
                      </span>
                    </p>
                  </div>
                  {step.status === 'loading' && (
                    <div 
                      className="flex-shrink-0"
                      aria-label="Loading"
                    >
                      <LoadingSpinner size="sm" color="blue" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Retry Information */}
            {initializationState.status === 'retrying' && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-amber-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    Retry attempt {initializationState.retryCount} of 3
                  </p>
                </div>
              </div>
            )}

            {/* Timeout Warning */}
            {timeoutWarning && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
                      Taking longer than expected
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300">
                      Elapsed time: {formatElapsedTime(elapsedTime)}. This may be due to high server load or network conditions.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Loading Time Display */}
            {elapsedTime > 5000 && (
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Loading time: {formatElapsedTime(elapsedTime)}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InitializationProgress;
